import type { ReactElement } from 'react';

import {
  displayRejectionComment,
  displayRejectionReason,
  formatRejectionWhen,
  hasRejectionDetails,
  isRejectedSubject,
  reviewerPartyLabel,
  type SubjectRejectionFields,
} from '../lib/subject-outcome';

export function RejectedSubjectsNotes({
  subjects,
  showWhenEmpty = false,
  standalone = false,
}: {
  subjects: SubjectRejectionFields[];
  /** When true, render an empty state instead of hiding entirely. */
  showWhenEmpty?: boolean;
  /** Tab layout — drops extra inset spacing. */
  standalone?: boolean;
}): ReactElement | null {
  const rejected = subjects.filter(isRejectedSubject);
  if (rejected.length === 0) {
    if (!showWhenEmpty) return null;
    return (
      <div className="empty-state">
        <p className="h2">No rejection notes</p>
        <p className="text-muted">
          When a subject is not approved, the review team&apos;s reason and comments will appear here.
        </p>
      </div>
    );
  }

  return (
    <section
      className={`subject-rejection-panel${standalone ? ' subject-rejection-panel--standalone' : ''}`}
      aria-labelledby="subject-rejection-heading"
    >
      <div className="subject-rejection-panel-head">
        <h3 className="h3" id="subject-rejection-heading">Not approved — review notes</h3>
        <p className="text-muted">
          These subjects were not approved. Review the reason and comments below before revising
          your choices.
        </p>
      </div>
      <ul className="subject-rejection-list">
        {rejected.map((subject) => (
          <li key={subject.id} className="subject-rejection-card">
            <div className="subject-rejection-card-head">
              <div>
                <strong>{subject.subjectName}</strong>
                {subject.syllabusCode ? (
                  <span className="subject-syllabus-code">{subject.syllabusCode}</span>
                ) : null}
              </div>
              {subject.rejectedAt ? (
                <time className="subject-rejection-when" dateTime={subject.rejectedAt}>
                  {formatRejectionWhen(subject.rejectedAt)}
                </time>
              ) : null}
            </div>
            {hasRejectionDetails(subject) ? (
              <dl className="subject-rejection-details">
                {displayRejectionReason(subject) ? (
                  <>
                    <dt>Reason</dt>
                    <dd>{displayRejectionReason(subject)}</dd>
                  </>
                ) : null}
                {displayRejectionComment(subject) ? (
                  <>
                    <dt>Comment</dt>
                    <dd>{displayRejectionComment(subject)}</dd>
                  </>
                ) : null}
                {reviewerPartyLabel(subject.reviewerParty) ? (
                  <>
                    <dt>Reviewed by</dt>
                    <dd>{reviewerPartyLabel(subject.reviewerParty)}</dd>
                  </>
                ) : null}
                {subject.stepName ? (
                  <>
                    <dt>Review step</dt>
                    <dd>{subject.stepName}</dd>
                  </>
                ) : null}
              </dl>
            ) : (
              <p className="text-muted subject-rejection-empty">
                No additional notes were recorded for this subject.
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
